import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { 
  CreditCard, 
  DollarSign, 
  ExternalLink, 
  Loader2, 
  AlertCircle,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { trackEvent } from '../utils/analytics';

// Use relative URLs for same-origin requests (no CORS)
const API_URL = '';
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_51Sjjw084BnLFnMoXQwORzJ7v6PwIDDXszQidyw7MOzenZxiwhYiUcsUdga7N8670Dr6XI0wyb2tTqHKLA1IfraBW00rn5HozK5';

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

// Payment amounts
const PAYMENT_AMOUNTS = {
  skip: 500,      // $5
  superskip: 1000, // $10
  custom: null     // Variable
};

// eslint-disable-next-line no-unused-vars
const _CUSTOM_TIP_MIN = 1500; // $15
// eslint-disable-next-line no-unused-vars
const _CUSTOM_TIP_INCREMENT = 500; // $5

// Stripe Payment Form Component
// eslint-disable-next-line no-unused-vars
const StripePaymentForm = ({ clientSecret, onSuccess, onCancel, amount, paymentId, onBeforeRedirect }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    // IMPORTANT: Save form state BEFORE initiating payment (in case of redirect)
    // This includes pre-uploading files for redirect-based payment methods
    if (onBeforeRedirect) {
      console.log('[STRIPE] Saving form state before payment confirmation...');
      try {
        const saveResult = await onBeforeRedirect({ payment_id: paymentId });
        // If saveResult is explicitly false, the file upload failed
        if (saveResult === false) {
          console.error('[STRIPE] Form state save failed (file upload error)');
          setError('Failed to prepare your file for submission. Please try again or use a streaming link instead.');
          setIsProcessing(false);
          return;
        }
      } catch (err) {
        console.error('[STRIPE] Error saving form state:', err);
        setError('Failed to prepare your submission. Please try again.');
        setIsProcessing(false);
        return;
      }
    }

    const { error: submitError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href, // Used when redirect is required (e.g., Cash App Pay)
      },
      redirect: 'if_required'
    });

    // If we get here, either:
    // 1. Payment succeeded without redirect
    // 2. Payment failed
    // 3. User cancelled
    // (If redirected, this code won&apos;t execute until user returns)

    if (submitError) {
      setError(submitError.message);
      setIsProcessing(false);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess(paymentIntent.id);
    } else {
      setError('Payment failed. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <PaymentElement />
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center text-red-700">
          <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      
      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isProcessing}
          className="flex-1"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={!stripe || isProcessing}
          className="flex-1 bg-[#F2D857] hover:bg-[#F2D857]/90 text-black"
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              Pay ${(amount / 100).toFixed(2)}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

// Main PaymentStep Component
const PaymentStep = ({ 
  reviewerId,
  sessionId, // Track which session this payment is for
  paymentType, // 'skip', 'superskip', 'custom'
  customAmount, // For custom tips (in cents)
  onPaymentComplete, 
  onCancel,
  onDowngradeToFree,
  onBeforeStripeRedirect // NEW: Callback to save form state before redirect
}) => {
  const [paymentMethod, setPaymentMethod] = useState(null); // 'stripe' or 'direct' (cash_app/venmo)
  const [selectedDirectMethod, setSelectedDirectMethod] = useState('cash_app'); // 'cash_app' or 'venmo'
  const [reviewerInfo, setReviewerInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Stripe state
  const [clientSecret, setClientSecret] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [_paymentIntentId, setPaymentIntentId] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  
  // Cash App / Venmo state
  const [cashAppConfirmed, setCashAppConfirmed] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [confirmationNote, _setConfirmationNote] = useState('');
  const [artistPaymentName, setArtistPaymentName] = useState(''); // Artist's Cash App or Venmo name
  const [isConfirming, setIsConfirming] = useState(false);
  
  // For Pro Plan: track if user wants to use direct payment instead of Stripe
  const [useDirectPayment, setUseDirectPayment] = useState(false);

  // Get payment amount
  const amount = paymentType === 'custom' ? customAmount : PAYMENT_AMOUNTS[paymentType];

  const checkPaymentMethod = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/payments/reviewer-payment-method/${reviewerId}`);
      setReviewerInfo(response.data);
      setPaymentMethod(response.data.payment_method);
      
      // Check if Stripe is the method but not connected
      if (response.data.payment_method === 'stripe' && !response.data.stripe_connected) {
        // For Pro Plan with direct payment options available, fall back to direct payment
        if (response.data.is_pro_plan && (response.data.has_cash_app || response.data.has_venmo)) {
          setUseDirectPayment(true);
          setSelectedDirectMethod(response.data.has_cash_app ? 'cash_app' : 'venmo');
        } else if (response.data.is_pro_plan) {
          // Pro Plan but nothing set up
          setError('This reviewer hasn\'t set up their payment method yet. You can submit with No Tip instead.');
        } else {
          // Free Plan with no Stripe
          setError('This reviewer has not connected their Stripe account yet. You can submit with No Tip instead.');
        }
        return;
      }
      
      if (response.data.payment_method === 'stripe' && !useDirectPayment) {
        // Create PaymentIntent for Stripe
        await createStripePaymentIntent();
      }
    } catch (err) {
      console.error('Error checking payment method:', err);
      setError(err.response?.data?.detail || 'Failed to load payment options');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewerId, useDirectPayment]);

  useEffect(() => {
    checkPaymentMethod();
  }, [checkPaymentMethod]);

  const createStripePaymentIntent = useCallback(async () => {
    try {
      const response = await axios.post(`${API_URL}/api/payments/create-intent`, {
        reviewer_id: reviewerId,
        session_id: sessionId, // Track session for earnings
        payment_type: paymentType,
        custom_amount: paymentType === 'custom' ? customAmount : undefined
      });
      
      setClientSecret(response.data.client_secret);
      setPaymentIntentId(response.data.payment_intent_id);
      setPaymentId(response.data.payment_id);
    } catch (err) {
      console.error('Error creating payment intent:', err);
      setError(err.response?.data?.detail || 'Failed to create payment');
    }
  }, [reviewerId, sessionId, paymentType, customAmount]);

  const handleStripeSuccess = async (paymentIntentId) => {
    try {
      // Confirm payment on backend
      const response = await axios.post(
        `${API_URL}/api/payments/confirm-stripe?payment_intent_id=${paymentIntentId}`
      );
      
      onPaymentComplete({
        payment_id: response.data.payment_id,
        payment_method: 'stripe',
        amount: amount
      });
    } catch (err) {
      console.error('Error confirming payment:', err);
      setError(err.response?.data?.detail || 'Failed to confirm payment');
    }
  };

  const handleCashAppConfirm = async () => {
    if (!cashAppConfirmed) {
      setError('Please confirm that you have sent the payment');
      return;
    }
    
    if (!artistPaymentName.trim()) {
      setError(`Please enter your ${selectedDirectMethod === 'venmo' ? 'Venmo' : 'Cash App'} name so the reviewer can match your payment`);
      return;
    }
    
    setIsConfirming(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/payments/confirm-cashapp`, {
        reviewer_id: reviewerId,
        session_id: sessionId, // Track session for earnings
        payment_type: paymentType,
        custom_amount: paymentType === 'custom' ? customAmount : undefined,
        confirmation_note: confirmationNote || undefined,
        artist_cash_app_name: artistPaymentName.trim(), // Include artist's payment name (works for both)
        payment_method_type: selectedDirectMethod, // 'cash_app' or 'venmo'
        confirmed: true
      });
      
      onPaymentComplete({
        payment_id: response.data.payment_id,
        payment_method: selectedDirectMethod,
        amount: amount
      });
    } catch (err) {
      console.error('Error confirming payment:', err);
      setError(err.response?.data?.detail || 'Failed to confirm payment');
    } finally {
      setIsConfirming(false);
    }
  };

  const getCashAppUrl = () => {
    // cash_app_handle could be a full URL like "https://cash.app/$jxhnjacxb"
    // or just a handle like "$jxhnjacxb" or "jxhnjacxb"
    const handle = reviewerInfo?.cash_app_handle || '';
    
    // Extract just the username from the URL or handle
    let username = handle;
    if (handle.includes('cash.app/')) {
      // Extract username from URL: https://cash.app/$jxhnjacxb -> jxhnjacxb
      username = handle.split('cash.app/')[1] || '';
    }
    // Remove any $ prefix
    username = username.replace(/^\$/, '');
    
    // Cash App payment URL format: https://cash.app/$username/amount
    const amountDollars = (amount / 100).toFixed(2);
    return `https://cash.app/$${username}/${amountDollars}`;
  };
  
  // Get a display-friendly version of the Cash App handle
  const getCashAppDisplayHandle = () => {
    const handle = reviewerInfo?.cash_app_handle || '';
    if (handle.includes('cash.app/')) {
      // Extract from URL
      const username = handle.split('cash.app/')[1] || '';
      return username.startsWith('$') ? username : `$${username}`;
    }
    return handle.startsWith('$') ? handle : `$${handle}`;
  };

  // Venmo URL helper
  const getVenmoUrl = () => {
    const handle = reviewerInfo?.venmo_handle || '';
    let username = handle;
    if (handle.includes('venmo.com/')) {
      username = handle.split('venmo.com/')[1] || '';
    }
    username = username.replace(/^@/, '');
    const amountDollars = (amount / 100).toFixed(2);
    // Venmo payment URL format
    return `https://venmo.com/${username}?txn=pay&amount=${amountDollars}`;
  };

  // Get a display-friendly version of the Venmo handle
  const getVenmoDisplayHandle = () => {
    const handle = reviewerInfo?.venmo_handle || '';
    if (handle.includes('venmo.com/')) {
      const username = handle.split('venmo.com/')[1] || '';
      return username.startsWith('@') ? username : `@${username}`;
    }
    return handle.startsWith('@') ? handle : `@${handle}`;
  };

  if (isLoading) {
    return (
      <Card className="p-6 bg-gray-900 border-gray-700">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-[#F2D857]" />
          <span className="ml-3 text-gray-300">Loading payment options...</span>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-gray-900 border-gray-700">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-white mb-2">
          Complete Payment
        </h3>
        <p className="text-gray-400">
          {paymentType === 'skip' && 'Skip the Line - $5.00'}
          {paymentType === 'superskip' && 'Super Skip - $10.00'}
          {paymentType === 'custom' && `Custom Tip - $${(amount / 100).toFixed(2)}`}
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4 mb-6 flex items-start">
          <AlertCircle className="h-5 w-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-300">{error}</p>
            {error.includes('Stripe account') && (
              <p className="text-sm text-red-400 mt-1">
                This reviewer has not set up payments yet. You can submit with No Tip instead.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Stripe Payment */}
      {paymentMethod === 'stripe' && clientSecret && !useDirectPayment && (
        <div className="space-y-4">
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <CreditCard className="h-5 w-5 text-blue-400 mr-2" />
                <span className="font-semibold text-blue-300">Pay with Card</span>
              </div>
              {/* Pro Plan badge - no fees! */}
              {reviewerInfo?.is_pro_plan && reviewerInfo?.platform_fee_percentage === 0 && (
                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                  0% fees
                </span>
              )}
            </div>
            <p className="text-sm text-gray-400">
              Secure payment via Stripe. Automatic &amp; instant.
            </p>
          </div>
          
          <Elements 
            stripe={stripePromise} 
            options={{ 
              clientSecret,
              appearance: {
                theme: 'night',
                variables: {
                  colorPrimary: '#F2D857',
                  colorBackground: '#1a1a1a',
                  colorText: '#ffffff',
                  colorDanger: '#ef4444',
                  fontFamily: 'system-ui, sans-serif',
                  borderRadius: '8px',
                }
              }
            }}
          >
            <StripePaymentForm 
              clientSecret={clientSecret}
              onSuccess={handleStripeSuccess}
              onCancel={onCancel}
              amount={amount}
              paymentId={paymentId}
              onBeforeRedirect={onBeforeStripeRedirect}
            />
          </Elements>
          
          {/* Option to pay directly via Cash App/Venmo (Pro Plan only) */}
          {reviewerInfo?.is_pro_plan && (reviewerInfo?.has_cash_app || reviewerInfo?.has_venmo) && (
            <div className="border-t border-gray-700 pt-4 mt-4">
              <button
                onClick={() => {
                  const method = reviewerInfo?.has_cash_app ? 'cash_app' : 'venmo';
                  trackEvent('support_click', { method: method });
                  setUseDirectPayment(true);
                  setSelectedDirectMethod(method);
                }}
                className="w-full text-center text-sm text-gray-400 hover:text-gray-300 transition-colors"
              >
                Or pay directly via {reviewerInfo?.has_cash_app && reviewerInfo?.has_venmo 
                  ? 'Cash App / Venmo' 
                  : reviewerInfo?.has_cash_app ? 'Cash App' : 'Venmo'} →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Cash App / Venmo Payment (Direct Payment for Pro Plan) */}
      {((paymentMethod === 'stripe' && useDirectPayment && reviewerInfo?.is_pro_plan) || 
        (paymentMethod === 'cash_app')) && (
        <div className="space-y-4">
          {/* Back to Card option for Pro Plan users */}
          {reviewerInfo?.is_pro_plan && reviewerInfo?.stripe_connected && useDirectPayment && (
            <button
              onClick={async () => {
                setUseDirectPayment(false);
                setCashAppConfirmed(false);
                setArtistPaymentName('');
                // Re-create Stripe payment intent
                await createStripePaymentIntent();
              }}
              className="w-full text-center text-sm text-gray-400 hover:text-gray-300 transition-colors mb-2"
            >
              ← Back to Card Payment
            </button>
          )}
          
          {/* Payment Method Toggle - Only show if both methods available */}
          {reviewerInfo?.has_cash_app && reviewerInfo?.has_venmo && (
            <div className="flex rounded-lg overflow-hidden border border-gray-600 mb-2">
              <button
                onClick={() => {
                  trackEvent('support_click', { method: 'cash_app' });
                  setSelectedDirectMethod('cash_app');
                  setCashAppConfirmed(false);
                  setArtistPaymentName('');
                }}
                className={`flex-1 py-2.5 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  selectedDirectMethod === 'cash_app'
                    ? 'bg-[#00D632] text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                <DollarSign className="h-4 w-4" />
                Cash App
              </button>
              <button
                onClick={() => {
                  trackEvent('support_click', { method: 'venmo' });
                  setSelectedDirectMethod('venmo');
                  setCashAppConfirmed(false);
                  setArtistPaymentName('');
                }}
                className={`flex-1 py-2.5 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  selectedDirectMethod === 'venmo'
                    ? 'bg-[#008CFF] text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                <span className="text-base">Ⓥ</span>
                Venmo
              </button>
            </div>
          )}

          {/* Cash App Content */}
          {selectedDirectMethod === 'cash_app' && reviewerInfo?.has_cash_app && (
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <DollarSign className="h-5 w-5 text-green-400 mr-2" />
                <span className="font-semibold text-green-300">Pay with Cash App</span>
              </div>
              
              {/* Prominent Cash App Handle Display */}
              <div className="bg-black/40 rounded-lg p-4 mb-4 text-center">
                <p className="text-gray-400 text-xs mb-1">Send ${(amount / 100).toFixed(2)} to:</p>
                <p className="text-green-400 text-2xl font-bold">{getCashAppDisplayHandle()}</p>
                <p className="text-gray-500 text-xs mt-1">{reviewerInfo?.reviewer_name}</p>
              </div>
              
              {/* Two payment options */}
              <div className="space-y-2">
                <a
                  href={getCashAppUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-full py-2.5 px-4 bg-[#00D632] hover:bg-[#00B82B] text-white font-semibold rounded-lg transition-colors text-sm"
                >
                  <span className="mr-2">Open Cash App</span>
                  <ExternalLink className="h-4 w-4" />
                </a>
                <p className="text-center text-xs text-gray-500">
                  Or search <span className="text-green-400 font-medium">{getCashAppDisplayHandle()}</span> in your Cash App
                </p>
              </div>
              
              <p className="text-center text-xs text-blue-300 mt-3">
                📱 Come back here after sending to confirm
              </p>
            </div>
          )}

          {/* Venmo Content */}
          {selectedDirectMethod === 'venmo' && reviewerInfo?.has_venmo && (
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <span className="text-xl text-blue-400 mr-2">Ⓥ</span>
                <span className="font-semibold text-blue-300">Pay with Venmo</span>
              </div>
              
              {/* Prominent Venmo Handle Display */}
              <div className="bg-black/40 rounded-lg p-4 mb-4 text-center">
                <p className="text-gray-400 text-xs mb-1">Send ${(amount / 100).toFixed(2)} to:</p>
                <p className="text-blue-400 text-2xl font-bold">{getVenmoDisplayHandle()}</p>
                <p className="text-gray-500 text-xs mt-1">{reviewerInfo?.reviewer_name}</p>
              </div>
              
              {/* Two payment options */}
              <div className="space-y-2">
                <a
                  href={getVenmoUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-full py-2.5 px-4 bg-[#008CFF] hover:bg-[#0070CC] text-white font-semibold rounded-lg transition-colors text-sm"
                >
                  <span className="mr-2">Open Venmo</span>
                  <ExternalLink className="h-4 w-4" />
                </a>
                <p className="text-center text-xs text-gray-500">
                  Or search <span className="text-blue-400 font-medium">{getVenmoDisplayHandle()}</span> in your Venmo app
                </p>
              </div>
              
              <p className="text-center text-xs text-blue-300 mt-3">
                📱 Come back here after sending to confirm
              </p>
            </div>
          )}

          {/* Confirmation - Works for both Cash App and Venmo */}
          <div className="border-t border-gray-700 pt-4 space-y-4">
            {/* Artist Payment Name - Required */}
            <div>
              <Label htmlFor="artist_payment_name" className="text-gray-200 text-sm font-medium mb-2 block">
                Your {selectedDirectMethod === 'venmo' ? 'Venmo' : 'Cash App'} Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="artist_payment_name"
                placeholder={selectedDirectMethod === 'venmo' ? 'e.g., @JohnDoe' : 'e.g., $JohnDoe'}
                value={artistPaymentName}
                onChange={(e) => setArtistPaymentName(e.target.value)}
                className="bg-gray-800 border-gray-600 text-white"
              />
              <p className="text-xs text-gray-500 mt-1">
                So the reviewer can match your payment
              </p>
            </div>
            
            {/* Confirmation checkbox */}
            <div className="flex items-start space-x-3">
              <Checkbox
                id="payment_confirm"
                checked={cashAppConfirmed}
                onCheckedChange={setCashAppConfirmed}
                className="mt-1"
              />
              <Label htmlFor="payment_confirm" className="text-gray-300 cursor-pointer text-sm">
                I sent ${(amount / 100).toFixed(2)} to{' '}
                <span className={selectedDirectMethod === 'venmo' ? 'text-blue-400' : 'text-green-400'} style={{ fontWeight: 500 }}>
                  {selectedDirectMethod === 'venmo' ? getVenmoDisplayHandle() : getCashAppDisplayHandle()}
                </span>
              </Label>
            </div>
            
            {/* Notice about verification */}
            <div className="text-xs space-y-1">
              <p className="text-yellow-300/80">
                ⚠️ Your track will get priority once the reviewer confirms payment
              </p>
              <p className="text-gray-500">
                If not confirmed, your track will still be submitted as a regular (free) submission
              </p>
            </div>
            
            {/* Action buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={onCancel}
                className="flex-1 border-gray-600"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleCashAppConfirm}
                disabled={!cashAppConfirmed || isConfirming}
                className="flex-1 bg-[#F2D857] hover:bg-[#F2D857]/90 text-black"
              >
                {isConfirming ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Confirming...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Submit Track
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Downgrade Option */}
      <div className="mt-6 pt-4 border-t border-gray-700">
        <p className="text-center text-sm text-gray-500">
          Changed your mind?{' '}
          <button
            onClick={onDowngradeToFree}
            className="text-[#F2D857] hover:underline"
          >
            Submit with No Tip instead
          </button>
        </p>
      </div>
    </Card>
  );
};

export default PaymentStep;
